import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Button, Checkbox, ListItemText, MenuItem, Select, Tab, TableCell, TableRow, Tabs, TextField,
} from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import DataTable from '../../components/DataTable/DataTable';
import { apiList, verifyLogin } from '../../helpers';
import MonthlyBars from './MonthlyBars';
import downloadExcel, { activitiesOf } from './billingExcel';
import '../../assets/style/billingReport.scss';

// Billing report: per month, the loans with at least one verified activity
// (with photos) - the number each client is billed on. Every verification
// counts in the month it happened, even if the activity was later rejected,
// re-verified, its plan reset or its loan deleted (hfcapi src/web/billing).
//
// Period: this month so far (default), last month, or any months picked from
// the last three years. Tabs: trend + month totals, branches, verifications,
// billable loans. "Download Excel" exports the period (billingExcel.js).

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');
const monthName = (m) => moment(m, 'YYYY-MM').format('MMMM YYYY');
const monthShort = (m) => moment(m, 'YYYY-MM').format('MMM YYYY');
const shortDate = (d) => moment(d, 'YYYY-MM-DD HH:mm:ss').format('D MMM, h:mm a');

const get = (url, params) => {
  const { session } = verifyLogin();
  return axios.get(url, { headers: { session }, params }).then((r) => r.data.data);
};
const errorOf = (e) => (e.response && e.response.data && e.response.data.message)
  || 'Could not load the billing report. Please try again.';

const thisMonth = () => moment().utcOffset(330).format('YYYY-MM');
const shift = (m, n) => moment(m, 'YYYY-MM').add(n, 'months').format('YYYY-MM');
const PICKABLE_MONTHS = 36;
const pickable = () => Array.from({ length: PICKABLE_MONTHS }, (_, i) => shift(thisMonth(), -i));

const PERIODS = [
  { value: 'mtd', label: 'MTD' },
  { value: 'last', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
];

const MONTH_COLUMNS = [
  { id: 'month', label: 'Month' },
  { id: 'loans', label: 'Billable loans', align: 'right' },
  { id: 'activities', label: 'Verified activities', align: 'right' },
];
const BRANCH_COLUMNS = [
  { id: 'branch', label: 'Branch' },
  { id: 'state', label: 'State' },
  { id: 'loans', label: 'Billable loans', align: 'right' },
  { id: 'activities', label: 'Verified activities', align: 'right' },
];
const billableColumns = (withMonth) => [
  ...(withMonth ? [{ id: 'month', label: 'Month' }] : []),
  { id: 'loan', label: 'Loan number' },
  { id: 'customer', label: 'Customer' },
  { id: 'branch', label: 'Branch' },
  { id: 'state', label: 'State' },
  { id: 'activities', label: 'Activities verified', align: 'right' },
];
const loanColumns = (withMonth) => [
  ...(withMonth ? [{ id: 'month', label: 'Month' }] : []),
  { id: 'loan', label: 'Loan number' },
  { id: 'customer', label: 'Customer' },
  { id: 'branch', label: 'Branch' },
  { id: 'activities', label: 'Verified activities', minWidth: 260 },
];

const matches = (term, values) => !term
  || values.some((v) => String(v || '').toLowerCase().includes(term));

export default function BillingReport() {
  const { user } = verifyLogin();
  const isSuperAdmin = Number(user.userRoleId) === 1;

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isSuperAdmin ? '' : user.subscriberId);
  const [period, setPeriod] = useState('mtd');
  const [custom, setCustom] = useState([shift(thisMonth(), -1)]);
  const [tab, setTab] = useState(0);
  const [trend, setTrend] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const [loanSearch, setLoanSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const tabsActions = useRef(null);

  const months = useMemo(() => {
    if (period === 'mtd') return [thisMonth()];
    if (period === 'last') return [shift(thisMonth(), -1)];
    return [...custom].sort();
  }, [period, custom]);
  const monthsKey = months.join(',');
  const latest = months[months.length - 1];

  useEffect(() => {
    get(apiList.BILLING_SUBSCRIBERS).then((list) => {
      setClients(list || []);
      if (isSuperAdmin && list && list.length) setClientId(list[0].id);
    }).catch((e) => setError(errorOf(e)));
  }, [isSuperAdmin]);

  // Trend: the 12 months up to the latest selected month.
  useEffect(() => {
    if (!clientId || !latest) return;
    get(apiList.BILLING_SUMMARY, { subscriberId: clientId, from: shift(latest, -11), to: latest })
      .then(setTrend)
      .catch((e) => { setTrend(null); setError(errorOf(e)); });
  }, [clientId, latest]);

  useEffect(() => {
    if (!clientId || !monthsKey) { setReport(null); return; }
    setLoading(true);
    setError('');
    get(apiList.BILLING_REPORT, { subscriberId: clientId, months: monthsKey })
      .then(setReport)
      .catch((e) => { setReport(null); setError(errorOf(e)); })
      .finally(() => setLoading(false));
  }, [clientId, monthsKey]);

  // Tab labels carry counts; re-place the underline when their width changes.
  useEffect(() => {
    if (tabsActions.current) tabsActions.current.updateIndicator();
  }, [report]);

  const client = clients.find((c) => String(c.id) === String(clientId));
  const clientName = client ? client.name : '';
  const totals = report ? report.months.reduce((t, m) => ({
    loans: t.loans + m.loans, activities: t.activities + m.activities,
  }), { loans: 0, activities: 0 }) : null;
  const multi = months.length > 1;
  // Name up to three months; more as a count with the first and last.
  let periodLabel = months.map(monthShort).join(', ');
  if (months.length === 1) periodLabel = monthName(months[0]);
  if (months.length > 3) {
    periodLabel = `${months.length} months, ${monthShort(months[0])} – ${monthShort(latest)}`;
  }

  const branches = useMemo(() => {
    const term = branchSearch.trim().toLowerCase();
    if (!report) return [];
    return report.branches.filter((b) => matches(term, [b.branchName, b.state, b.ifsc]));
  }, [report, branchSearch]);
  const loans = useMemo(() => {
    const term = loanSearch.trim().toLowerCase();
    if (!report) return [];
    return report.loans.filter((l) => matches(term, [
      l.loanNumber, l.custName, l.branchName, l.state,
    ]));
  }, [report, loanSearch]);

  const openBranch = (b) => {
    setLoanSearch(b.branchName);
    setTab(2);
  };
  const exportExcel = () => {
    setExporting(true);
    downloadExcel({
      report, client: clientName, period: periodLabel, months,
    })
      .catch(() => setError('Could not create the Excel file. Please try again.'))
      .finally(() => setExporting(false));
  };
  const pickMonth = (m) => {
    setCustom([m]);
    setPeriod('custom');
  };

  return (
    <div className='br-page'>
      <section className='br-card br-head'>
        <div className='br-title'>
          <h1>Billing Report</h1>
          <p>
            Loans with at least one verified activity (with photos) in the month. A verification
            counts in the month it happened, even if the activity is later rejected, re-verified
            or its plan is reset.
          </p>
        </div>
        <div className='br-controls'>
          {isSuperAdmin ? (
            <TextField
              select
              size='small'
              variant='outlined'
              label='Client'
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className='br-client'
            >
              {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          ) : null}
          <ToggleButtonGroup
            className='br-period'
            size='small'
            exclusive
            value={period}
            onChange={(e, value) => { if (value) setPeriod(value); }}
            aria-label='Period'
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          {period === 'custom' ? (
            <Select
              multiple
              variant='outlined'
              className='br-months'
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected.length) return 'Choose months';
                if (selected.length <= 2) return [...selected].sort().map(monthShort).join(', ');
                return `${selected.length} months selected`;
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } }, getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }}
              inputProps={{ 'aria-label': 'Months' }}
            >
              {pickable().map((m) => (
                <MenuItem key={m} value={m} dense>
                  <Checkbox size='small' color='primary' checked={custom.includes(m)} />
                  <ListItemText primary={monthName(m)} secondary={m === thisMonth() ? 'In progress' : null} />
                </MenuItem>
              ))}
            </Select>
          ) : null}
        </div>
      </section>

      {error ? <div className='br-error' role='alert'>{error}</div> : null}

      <section className='br-card br-summary' aria-live='polite'>
        <div>
          <span className='br-summary-label'>Period</span>
          <b>{months.length ? periodLabel : '—'}</b>
          {months.includes(thisMonth()) ? <span className='br-tag'>In progress</span> : null}
        </div>
        <div>
          <span className='br-summary-label'>Billable loans</span>
          <b className='br-big'>{totals ? fmt(totals.loans) : '—'}</b>
          {multi && totals ? <span className='br-sub'>sum of the months</span> : null}
        </div>
        <div>
          <span className='br-summary-label'>Verified activities</span>
          <b className='br-big'>{totals ? fmt(totals.activities) : '—'}</b>
        </div>
        <Button
          className='br-export'
          variant='contained'
          color='primary'
          startIcon={<GetAppRoundedIcon />}
          disabled={!report || loading || exporting || !report.loans.length}
          onClick={exportExcel}
        >
          {exporting ? 'Preparing…' : 'Download Excel'}
        </Button>
      </section>

      {!months.length ? (
        <div className='br-error' role='alert'>Choose at least one month.</div>
      ) : (
        <section className='br-card br-tabs-card'>
          <Tabs
            action={tabsActions}
            value={tab}
            onChange={(e, v) => setTab(v)}
            indicatorColor='primary'
            textColor='primary'
            className='br-tabs'
          >
            <Tab label='Trend' />
            <Tab label={`By branch${report ? ` (${fmt(report.branches.length)})` : ''}`} />
            <Tab label='Loans & activities' />
            <Tab label={`Billable loans${report ? ` (${fmt(report.loans.length)})` : ''}`} />
          </Tabs>

          {tab === 0 ? (
            <div className='br-tab'>
              <div className='br-card-head'>
                <h2>Billable loans per month</h2>
                <span className='br-hint'>12 months to the selected period · click a month to select it</span>
              </div>
              {trend ? (
                <MonthlyBars months={trend.months} selected={months} onSelect={pickMonth} />
              ) : <div className='br-chart br-chart-empty'>Loading…</div>}
              <DataTable
                bare
                paginate={false}
                loading={loading}
                tableHead={MONTH_COLUMNS}
                noDataText='No months to show.'
                itemComponent={report ? [
                  ...report.months.slice().reverse().map((m) => (
                    <TableRow key={m.month}>
                      <TableCell>
                        {monthName(m.month)}
                        {m.month === thisMonth() ? <span className='br-tag'>In progress</span> : null}
                      </TableCell>
                      <TableCell align='right'>{fmt(m.loans)}</TableCell>
                      <TableCell align='right'>{fmt(m.activities)}</TableCell>
                    </TableRow>
                  )),
                  ...(multi ? [(
                    <TableRow key='total' className='dt-total'>
                      <TableCell>Total (sum of months)</TableCell>
                      <TableCell align='right'>{fmt(totals.loans)}</TableCell>
                      <TableCell align='right'>{fmt(totals.activities)}</TableCell>
                    </TableRow>
                  )] : []),
                ] : []}
              />
            </div>
          ) : null}

          {tab === 1 ? (
            <div className='br-tab'>
              <DataTable
                bare
                title={multi ? 'Billable loans per branch, summed over the months' : 'Billable loans per branch'}
                loading={loading}
                search={{ placeholder: 'Search branch or state', value: branchSearch, onChange: (e) => setBranchSearch(e.target.value) }}
                tableHead={BRANCH_COLUMNS}
                noDataText={branchSearch ? 'No branches match your search.' : 'No billable loans in this period.'}
                itemComponent={branches.map((b) => (
                  <TableRow
                    key={b.ifsc || b.branchName}
                    hover
                    className='br-row-link'
                    title='Show this branch’s loans'
                    onClick={() => openBranch(b)}
                  >
                    <TableCell>{b.branchName}</TableCell>
                    <TableCell>{b.state || '—'}</TableCell>
                    <TableCell align='right'>{fmt(b.loans)}</TableCell>
                    <TableCell align='right'>{fmt(b.activities)}</TableCell>
                  </TableRow>
                ))}
              />
            </div>
          ) : null}

          {tab === 2 ? (
            <div className='br-tab'>
              <DataTable
                bare
                title={multi ? 'Verified activities by loan, per month' : 'Verified activities by loan'}
                loading={loading}
                search={{ placeholder: 'Search loan, customer or branch', value: loanSearch, onChange: (e) => setLoanSearch(e.target.value) }}
                tableHead={loanColumns(multi)}
                noDataText={loanSearch ? 'No loans match your search.' : 'No billable loans in this period.'}
                itemComponent={loans.map((l) => (
                  <TableRow key={l.key}>
                    {multi ? <TableCell className='br-nowrap'>{monthShort(l.month)}</TableCell> : null}
                    <TableCell className='br-nowrap'>
                      {l.loanDeleted ? (
                        <span className='br-deleted' title={`Loan deleted; property ${l.propertyId}`}>Deleted loan</span>
                      ) : l.loanNumber}
                    </TableCell>
                    <TableCell>{l.custName || '—'}</TableCell>
                    <TableCell>
                      {l.branchName}
                      {l.state ? <span className='br-sub'>{l.state}</span> : null}
                    </TableCell>
                    <TableCell>
                      {l.verifications.map((v) => (
                        <div key={`${v.planId}-${v.verifiedAt}`} className='br-act'>
                          <span className='br-act-name'>{v.activity}</span>
                          <span className='br-sub'>
                            {[v.floor, shortDate(v.verifiedAt), v.verifiedBy].filter(Boolean).join(' · ')}
                          </span>
                          {v.planReset ? <span className='br-tag' title='The plan was reset after this verification'>Plan reset</span> : null}
                        </div>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              />
            </div>
          ) : null}

          {tab === 3 ? (
            <div className='br-tab'>
              <DataTable
                bare
                title={multi ? 'Billable loans, one row per loan per month' : 'Billable loans'}
                loading={loading}
                search={{ placeholder: 'Search loan, customer or branch', value: loanSearch, onChange: (e) => setLoanSearch(e.target.value) }}
                tableHead={billableColumns(multi)}
                noDataText={loanSearch ? 'No loans match your search.' : 'No billable loans in this period.'}
                itemComponent={loans.map((l) => (
                  <TableRow key={l.key}>
                    {multi ? <TableCell className='br-nowrap'>{monthShort(l.month)}</TableCell> : null}
                    <TableCell className='br-nowrap'>
                      {l.loanDeleted ? (
                        <span className='br-deleted' title={`Loan deleted; property ${l.propertyId}`}>Deleted loan</span>
                      ) : l.loanNumber}
                    </TableCell>
                    <TableCell>{l.custName || '—'}</TableCell>
                    <TableCell>{l.branchName}</TableCell>
                    <TableCell>{l.state || '—'}</TableCell>
                    <TableCell align='right'>{fmt(activitiesOf(l))}</TableCell>
                  </TableRow>
                ))}
              />
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
